
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue'
import CreditCardModalMagensa from '@/components/modals/CreditCardModalMagensa.vue'
import { createVuexHelpers } from 'vue2-helpers'
import { CreditCard } from '@/types/entitiesTypes'

const POPUP_CLOSE_ANIMATION_MS = 420
const POPUP_OPEN_ANIMATION_MS = 420
const SWIPE_CLOSE_MIN_DISTANCE_PX = 110
const CUSTOM_ROW_HEIGHT = 52
const CUSTOM_ROWS_MAX_VISIBLE = 4
const CUSTOM_ROWS_BOTTOM_Y = 828
const CUSTOM_ROWS_X = 42
const DEFAULT_CUSTOM_ROWS_WIDTH_PERCENT = 80
const COMPACT_CUSTOM_ROWS_WIDTH_PERCENT = 83
const COMPACT_LAYOUT_MAX_WIDTH = 410
const ROTATE_OVERLAY_MAX_WIDTH = 1024
const ROTATE_OVERLAY_MAX_HEIGHT = 560
const SVG_VIEWBOX_WIDTH = 430
const SVG_VIEWBOX_HEIGHT = 768
const PAYMENT_BLOCK_OFFSET_Y = 210
const MAGENSA_INSTANCE_WAIT_TIMEOUT_MS = 7000
const MAGENSA_INSTANCE_WAIT_STEP_MS = 100
const SWIPE_IGNORE_TARGET_SELECTOR = [
	'button',
	'a',
	'input',
	'textarea',
	'select',
	'option',
	'label',
	'[role="button"]',
	'[contenteditable="true"]',
	'.ticketing-smart-tab-locked-popup__custom-card-row',
	'.ticketing-smart-tab-locked-popup__add-card-row'
].join(',')

type CustomPaymentRow = {
	id: string
	label: string
	mask: string
	iconType: 'visa' | 'mc' | 'apple' | 'generic'
}

const PRESET_PAYMENT_ROWS: CustomPaymentRow[] = [
	{ id: 'preset-visa', label: 'VISA', mask: '···· 1111', iconType: 'visa' },
	{ id: 'preset-mc', label: 'M/C', mask: '···· 1111', iconType: 'mc' },
	{ id: 'preset-apple', label: 'Apple Pay', mask: '', iconType: 'apple' }
]

const getCardTypeMetaFromBin = (
	bin = ''
): Pick<CustomPaymentRow, 'label' | 'iconType'> | null => {
	const normalizedBin = String(bin).replace(/\D/g, '')
	if (!normalizedBin) return null

	if (normalizedBin.startsWith('4')) {
		return { label: 'VISA', iconType: 'visa' }
	}

	const firstTwo = Number(normalizedBin.slice(0, 2))
	const firstFour = Number(normalizedBin.slice(0, 4))
	const isMasterCardRangeByFirstTwo = firstTwo >= 51 && firstTwo <= 55
	const isMasterCardRangeByFirstFour = firstFour >= 2221 && firstFour <= 2720
	if (isMasterCardRangeByFirstTwo || isMasterCardRangeByFirstFour) {
		return { label: 'M/C', iconType: 'mc' }
	}

	return null
}

const getCardTypeMeta = (
	cardType = '',
	bin = ''
): Pick<CustomPaymentRow, 'label' | 'iconType'> => {
	const value = cardType.toLowerCase().replace(/[\s/_-]/g, '')

	if (value.includes('visa')) return { label: 'VISA', iconType: 'visa' }
	if (
		value.includes('master') ||
		value.includes('mastercard') ||
		value === 'mc' ||
		value.includes('m/c')
	) {
		return { label: 'M/C', iconType: 'mc' }
	}
	if (value.includes('apple')) return { label: 'Apple Pay', iconType: 'apple' }

	const detectedByBin = getCardTypeMetaFromBin(bin)
	if (detectedByBin) return detectedByBin

	return { label: 'CARD', iconType: 'generic' }
}

export default defineComponent({
	name: 'TicketingSmartTabLockedPopup',
	components: {
		CreditCardModalMagensa
	},

	emits: ['close'],

	setup(props, { emit }) {
		const { useActions, useGetters } = createVuexHelpers()
		const {
			mountTEconnect
		}: { mountTEconnect: () => void } = useActions('payment', [
			'mountTEconnect'
		])
		const {
			lockScroll,
			unlockScroll
		}: { lockScroll: () => void; unlockScroll: () => void } = useActions('ui', [
			'lockScroll',
			'unlockScroll'
		])
		const {
			magensaPaymentInstance
		}: {
			magensaPaymentInstance: { value: unknown }
		} = useGetters('payment', ['magensaPaymentInstance'])
		const {
			isScrollLocked
		}: {
			isScrollLocked: { value: boolean }
		} = useGetters('ui', ['isScrollLocked'])

		const animationState = ref<'closed' | 'opening' | 'open' | 'closing'>(
			'closed'
		)
		const isClosing = ref(false)
		const isAddCardModalVisible = ref(false)
		const isPreparingAddCardModal = ref(false)
		const suppressNextRootClose = ref(false)
		const isScrollLockedByPopup = ref(false)
		const isOrientationOverlayVisible = ref(false)
		const viewportWidth = ref(
			typeof window !== 'undefined' ? window.innerWidth : SVG_VIEWBOX_WIDTH
		)
		const selectedRowId = ref('preset-visa')
		const customPaymentRows = ref<CustomPaymentRow[]>([])
		const swipeOptions = Object.freeze({
			swipeTolerance: SWIPE_CLOSE_MIN_DISTANCE_PX
		})

		const popupStateClass = computed(
			() => `ticketing-smart-tab-locked-popup--${animationState.value}`
		)
		const paymentRows = computed(() => [
			...customPaymentRows.value,
			...PRESET_PAYMENT_ROWS
		])
		const paymentRowsVisibleCount = computed(() =>
			Math.min(paymentRows.value.length, CUSTOM_ROWS_MAX_VISIBLE)
		)
		const isPaymentRowsScrollable = computed(
			() => paymentRows.value.length > CUSTOM_ROWS_MAX_VISIBLE
		)
		const isCompactCardsLayout = computed(
			() => viewportWidth.value <= COMPACT_LAYOUT_MAX_WIDTH
		)
		const paymentRowsForeignObjectHeight = computed(
			() => paymentRowsVisibleCount.value * CUSTOM_ROW_HEIGHT
		)
		const paymentRowsForeignObjectY = computed(
			() => CUSTOM_ROWS_BOTTOM_Y - paymentRowsForeignObjectHeight.value
		)
		const paymentRowsOverlayStyle = computed(() => {
			const top = paymentRowsForeignObjectY.value - PAYMENT_BLOCK_OFFSET_Y
			const widthPercent = isCompactCardsLayout.value
				? COMPACT_CUSTOM_ROWS_WIDTH_PERCENT
				: DEFAULT_CUSTOM_ROWS_WIDTH_PERCENT

			return {
				left: `${(CUSTOM_ROWS_X / SVG_VIEWBOX_WIDTH) * 100}%`,
				width: `${widthPercent}%`,
				top: `${(top / SVG_VIEWBOX_HEIGHT) * 100}%`,
				height: `${(paymentRowsForeignObjectHeight.value / SVG_VIEWBOX_HEIGHT) *
					100}%`
			}
		})
		const updateViewportWidth = (): void => {
			viewportWidth.value = window.innerWidth
		}
		const updateOrientationOverlayVisibility = (): void => {
			const isLandscape = window.matchMedia('(orientation: landscape)').matches
			const isMobileLikeViewport =
				window.innerWidth <= ROTATE_OVERLAY_MAX_WIDTH &&
				window.innerHeight <= ROTATE_OVERLAY_MAX_HEIGHT

			isOrientationOverlayVisible.value = isLandscape && isMobileLikeViewport
		}

		onMounted(() => {
			if (!isScrollLocked.value) {
				lockScroll()
				isScrollLockedByPopup.value = true
			}
			updateViewportWidth()
			updateOrientationOverlayVisibility()
			window.addEventListener('resize', updateViewportWidth)
			window.addEventListener('resize', updateOrientationOverlayVisibility)
			window.addEventListener(
				'orientationchange',
				updateOrientationOverlayVisibility
			)

			requestAnimationFrame(() => {
				animationState.value = 'opening'
				window.setTimeout(() => {
					if (!isClosing.value) {
						animationState.value = 'open'
					}
				}, POPUP_OPEN_ANIMATION_MS)
			})
		})
		onUnmounted(() => {
			if (isScrollLockedByPopup.value) {
				unlockScroll()
			}
			window.removeEventListener('resize', updateViewportWidth)
			window.removeEventListener('resize', updateOrientationOverlayVisibility)
			window.removeEventListener(
				'orientationchange',
				updateOrientationOverlayVisibility
			)
		})

		const close = (): void => {
			if (suppressNextRootClose.value) {
				suppressNextRootClose.value = false

				return
			}

			if (
				isClosing.value ||
				isAddCardModalVisible.value ||
				isPreparingAddCardModal.value
			) {
				return
			}

			isClosing.value = true
			animationState.value = 'closing'
			window.setTimeout(() => {
				emit('close')
			}, POPUP_CLOSE_ANIMATION_MS)
		}
		const shouldIgnoreSwipeClose = (target: EventTarget | null): boolean => {
			if (!(target instanceof Element)) return false

			if (target.closest(SWIPE_IGNORE_TARGET_SELECTOR)) return true

			const cardsContainer = target.closest(
				'.ticketing-smart-tab-locked-popup__custom-cards'
			) as HTMLElement | null
			if (!cardsContainer) return false

			return cardsContainer.scrollHeight > cardsContainer.clientHeight
		}
		const onPanelSwipeBottom = (direction: string, event?: Event): void => {
			if (direction !== 'bottom') return
			if (event?.type?.includes('mouse')) return
			if (shouldIgnoreSwipeClose(event?.target ?? null)) return

			close()
		}

		const selectPaymentMethod = (rowId: string): void => {
			selectedRowId.value = rowId
		}

		const waitForMagensaInstance = (): Promise<boolean> =>
			new Promise(resolve => {
				const startedAt = Date.now()
				const intervalId = window.setInterval(() => {
					if (magensaPaymentInstance.value) {
						clearInterval(intervalId)
						resolve(true)

						return
					}

					if (Date.now() - startedAt >= MAGENSA_INSTANCE_WAIT_TIMEOUT_MS) {
						clearInterval(intervalId)
						resolve(false)
					}
				}, MAGENSA_INSTANCE_WAIT_STEP_MS)
			})

		const openAddCardModal = async (): Promise<void> => {
			if (isPreparingAddCardModal.value || isAddCardModalVisible.value) return

			isPreparingAddCardModal.value = true
			mountTEconnect()

			if (magensaPaymentInstance.value) {
				isAddCardModalVisible.value = true
				isPreparingAddCardModal.value = false

				return
			}

			const isInstanceReady = await waitForMagensaInstance()
			if (isInstanceReady) {
				isAddCardModalVisible.value = true
			}

			isPreparingAddCardModal.value = false
		}

		const closeAddCardModal = (): void => {
			suppressNextRootClose.value = true
			isAddCardModalVisible.value = false
			window.setTimeout(() => {
				suppressNextRootClose.value = false
			}, 200)
		}

		const onCreditCardAdded = (creditCard: CreditCard): void => {
			isAddCardModalVisible.value = false
			if (!creditCard?.id) return

			const rowId = `custom-${creditCard.id}`
			const isAlreadyAdded = customPaymentRows.value.some(
				row => row.id === rowId
			)
			if (!isAlreadyAdded) {
				const lastFour = String(creditCard.lastFour || '')
					.slice(-4)
					.padStart(4, '0')
				const { label, iconType } = getCardTypeMeta(
					creditCard.cardType,
					creditCard.bin
				)

				customPaymentRows.value.unshift({
					id: rowId,
					label,
					mask: `···· ${lastFour}`,
					iconType
				})
			}

			selectedRowId.value = rowId
		}

		return {
			popupStateClass,
			isOrientationOverlayVisible,
			selectedRowId,
			paymentRows,
			customPaymentRows,
			isPaymentRowsScrollable,
			isCompactCardsLayout,
			paymentRowsForeignObjectHeight,
			paymentRowsForeignObjectY,
			paymentRowsOverlayStyle,
			CUSTOM_ROWS_X,
			selectPaymentMethod,
			isAddCardModalVisible,
			isPreparingAddCardModal,
			openAddCardModal,
			closeAddCardModal,
			onCreditCardAdded,
			close,
			swipeOptions,
			onPanelSwipeBottom
		}
	}
})
